import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/brand";
import { Eyebrow, MaskLine, Reveal } from "./Reveal";

const MANTRAS = ["It's okay not to be okay.", "Talking is strength.", "Check in on your mates.", "One conversation can change everything.", "You matter more than you know."];

const Ticker = () => (
  <div className="relative h-56 overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_25%,black_75%,transparent)]">
    <div className="ticker-v flex flex-col gap-6">
      {[...MANTRAS, ...MANTRAS].map((m, i) => (
        <p key={i} className="font-display font-extrabold text-2xl sm:text-3xl text-slate-200">“{m}”</p>
      ))}
    </div>
  </div>
);

export const SpeakUp = () => {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      toast.error("Write at least a few words — we're listening.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/stories`, { name: name.trim() || null, message: message.trim() });
      toast.success(data?.message ?? "Thank you for speaking up.");
      setSent(true);
      setName("");
      setMessage("");
    } catch {
      toast.error("Couldn't send right now. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="speak-up" data-testid="speak-up-section" className="bg-[#090C11] py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Eyebrow>06 — Speak up</Eyebrow>
          <h2 className="mt-5 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight">
            <MaskLine inView>Say it here.</MaskLine>
            <MaskLine inView delay={0.1}><span className="text-[var(--mth-blue)]">Give it a voice.</span></MaskLine>
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-8 text-slate-300 text-base sm:text-lg max-w-md">Share your story, a moment that helped, or advice for the next bloke who's struggling. Stay anonymous if you like — every message is read and reviewed before anything is shared.</p>
          </Reveal>
          <div className="mt-10"><Ticker /></div>
        </div>
        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="speak-up-form" className="rounded-[28px] border border-white/10 bg-[#0B0F15] p-6 sm:p-10">
            <label className="block font-mono text-xs uppercase tracking-[0.22em] text-slate-300" htmlFor="su-name">First name or nickname (optional)</label>
            <input id="su-name" data-testid="speak-up-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Anonymous" className="mt-3 w-full bg-transparent border-b border-white/15 focus:border-[var(--mth-blue)] outline-none py-3 text-lg placeholder:text-slate-500 transition-colors" />
            <label className="mt-10 block font-mono text-xs uppercase tracking-[0.22em] text-slate-300" htmlFor="su-msg">Your story</label>
            <textarea id="su-msg" data-testid="speak-up-message-input" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1500} rows={7} placeholder="What do you wish someone had told you?" className="mt-3 w-full resize-none bg-transparent border-b border-white/15 focus:border-[var(--mth-blue)] outline-none py-3 text-lg placeholder:text-slate-500 transition-colors" />
            <div className="mt-3 flex justify-between font-mono text-[11px] uppercase tracking-widest text-slate-400">
              <span>Reviewed before sharing</span><span data-testid="speak-up-char-count">{message.length}/1500</span>
            </div>
            <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={busy} data-testid="speak-up-submit-button" className="glow-btn mt-10 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {sent ? "Share another" : "Share my story"}
            </motion.button>
            {sent && <p data-testid="speak-up-success" className="mt-6 text-[var(--mth-blue-soft)]">Thank you. Speaking up takes guts — we're proud of you.</p>}
          </form>
        </Reveal>
      </div>
    </section>
  );
};
